import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { ChatMessageResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { MessageItem } from './MessageItem';
import { useReplyNavigation } from '../hooks/useReplyNavigation';

interface MessageListProps {
  messages: ChatMessageResponse[];
  currentUserId: string | null;
  isAdmin?: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  /**
   * Frozen "last read" marker (epoch ms) for the channel. The first message
   * from another user newer than this gets a Telegram-style "Unread messages"
   * divider above it. Null/undefined → no divider.
   */
  unreadCutoff?: number | null;
  onLoadMore: () => void;
  onStartEditMessage?: (messageId: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onReplyMessage?: (messageId: string) => void;
  onReactionMessage?: (messageId: string, emoji: string) => void;
  onOpenDm?: (userId: string) => void;
}

export function MessageList({
  messages,
  currentUserId,
  isAdmin,
  loading,
  loadingMore,
  hasMore,
  unreadCutoff,
  onLoadMore,
  onStartEditMessage,
  onDeleteMessage,
  onReplyMessage,
  onReactionMessage,
  onOpenDm,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);
  // ID of the newest (bottom) message last render. Lets us tell a real append at
  // the bottom (new/sent message) from a prepend when loading older history —
  // only the former should auto-scroll.
  const lastMessageIdRef = useRef<string | null>(null);
  // Whether we've already positioned on the unread divider for this channel.
  const unreadScrollDoneRef = useRef(false);
  // The settled divider position, frozen the first render we have both a cutoff
  // and messages. Freezing means messages that arrive while you're reading don't
  // spawn or move the divider — it marks only what was unread when you opened
  // the channel ("since your last session"). Reset on channel (re)load.
  const settledUnreadRef = useRef<{ done: boolean; id: string | null }>({ done: false, id: null });

  // Candidate: the first message from someone else newer than the cutoff.
  // Mirrors the unread-badge rule (exclude own + deleted) so the two agree.
  const liveFirstUnreadId = useMemo(() => {
    if (unreadCutoff == null) return null;
    const m = messages.find(
      (msg) =>
        !msg.isDeleted &&
        msg.senderId !== currentUserId &&
        new Date(msg.createdAt).getTime() > unreadCutoff,
    );
    return m?.messageId ?? null;
  }, [messages, unreadCutoff, currentUserId]);

  if (loading) {
    if (settledUnreadRef.current.done) settledUnreadRef.current = { done: false, id: null };
  } else if (!settledUnreadRef.current.done && unreadCutoff != null && messages.length > 0) {
    settledUnreadRef.current = { done: true, id: liveFirstUnreadId };
  }
  const firstUnreadMessageId = settledUnreadRef.current.id;
  // Whether the user was pinned to the bottom as of their last scroll. Updated
  // on scroll (not on append), so when a new message arrives we know if they
  // were already at the bottom and want to follow it — versus reading or
  // selecting history above, where auto-scrolling would yank them away (and
  // wipe an in-progress selection). Also keeps "load older" from snapping down.
  const atBottomRef = useRef(true);
  // Drives the floating "down" button when the user has scrolled up away from the
  // newest message (distinct from the reply-jump return stack below). State rather
  // than a ref so the button's visibility re-renders as the user scrolls.
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);

  // Reply navigation: clicking a reply preview jumps to the quoted message
  // (located by its data-message-id) and highlights it; a floating button
  // walks back down through jumps Telegram-style.
  const { jumpToMessage, goBack, canGoBack, highlightedId, handleNavScroll } =
    useReplyNavigation(containerRef);

  // Scroll to bottom on initial load and when new messages arrive
  useEffect(() => {
    if (!loading && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      const lastId = lastMsg?.messageId ?? null;
      // A different bottom message means a real append at the end (someone sent a
      // message), not a prepend from loading older history.
      const appendedAtBottom = lastId !== lastMessageIdRef.current;

      if (prevMessageCountRef.current === 0) {
        // Initial load. Jump to the latest message — unless there's an unread
        // divider, in which case the effect below lands on it instead.
        if (!firstUnreadMessageId) {
          bottomRef.current?.scrollIntoView();
          atBottomRef.current = true;
          setShowJumpToBottom(false);
        }
      } else if (messages.length > prevMessageCountRef.current) {
        // The current user's own just-sent message should always pull them to the
        // bottom (e.g. after replying to someone while scrolled up reading). Other
        // people's messages only auto-follow when already pinned to the bottom, so
        // we don't yank a user who's reading/selecting history above.
        const ownJustSent = appendedAtBottom && lastMsg?.senderId === currentUserId;
        if (atBottomRef.current || ownJustSent) {
          bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
          atBottomRef.current = true;
          setShowJumpToBottom(false);
        } else if (appendedAtBottom) {
          // A new message landed at the bottom while the user is reading above —
          // make sure the jump-to-latest button is showing (a scroll event may
          // never fire, which is how the button went missing before).
          setShowJumpToBottom(true);
        }
      }
      lastMessageIdRef.current = lastId;
      prevMessageCountRef.current = messages.length;
    }
    // Reset when loading starts (channel switch)
    if (loading) {
      prevMessageCountRef.current = 0;
      lastMessageIdRef.current = null;
      atBottomRef.current = true;
      setShowJumpToBottom(false);
      unreadScrollDoneRef.current = false;
    }
  }, [loading, messages.length, firstUnreadMessageId, currentUserId]);

  // Land on the unread divider once it's known (Telegram-style). The cutoff
  // may arrive after the messages, so this runs separately from the initial
  // bottom-scroll and fires at most once per channel.
  useEffect(() => {
    if (loading || unreadScrollDoneRef.current || !firstUnreadMessageId) return;
    const target = containerRef.current?.querySelector(
      `[data-message-id="${firstUnreadMessageId}"]`,
    );
    if (target) {
      target.scrollIntoView({ block: 'center' });
      atBottomRef.current = false;
      // We deliberately landed away from the bottom; surface the jump-to-latest
      // button (no scroll event fires here to set it otherwise).
      setShowJumpToBottom(true);
      unreadScrollDoneRef.current = true;
    }
  }, [loading, firstUnreadMessageId, messages.length]);

  // Track scroll position (for the auto-follow decision), retire stale reply
  // return-points, and load more at the top.
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    atBottomRef.current = atBottom;
    setShowJumpToBottom(!atBottom);
    handleNavScroll();
    if (!hasMore || loadingMore) return;
    if (el.scrollTop < 100) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore, handleNavScroll]);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-lg px-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="h-8 w-8 bg-muted rounded-full" />
              <div className="flex-1">
                <div className="h-3 bg-muted rounded w-20 mb-2" />
                <div className="h-4 bg-muted rounded w-48" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No messages yet</p>
          <p className="text-sm mt-1">Start the conversation!</p>
        </div>
      </div>
    );
  }

  let lastDate = '';

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-0 py-2">
        {loadingMore && (
          <div className="flex justify-center py-2">
            <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {messages.map((message, index) => {
          const messageDate = new Date(message.createdAt).toLocaleDateString();
          const showDateSeparator = messageDate !== lastDate;
          lastDate = messageDate;

          const prev = index > 0 ? messages[index - 1] : null;
          const next = index < messages.length - 1 ? messages[index + 1] : null;

          const isGroupMember = (
            a: (typeof messages)[0],
            b: (typeof messages)[0],
            sameDate: boolean,
          ) =>
            sameDate &&
            !a.isDeleted &&
            !b.isDeleted &&
            a.messageType !== 'system' &&
            b.messageType !== 'system' &&
            a.senderId === b.senderId;

          const isConsecutive =
            prev !== null && !showDateSeparator && isGroupMember(prev, message, true);

          const nextDate = next ? new Date(next.createdAt).toLocaleDateString() : null;
          const isLastInGroup = !next || !isGroupMember(message, next, nextDate === messageDate);

          return (
            <div
              key={message.messageId}
              data-message-id={message.messageId}
              className={cn(
                'rounded-lg transition-colors duration-700',
                highlightedId === message.messageId && 'bg-primary/10',
              )}
            >
              {showDateSeparator && (
                <div className="flex items-center gap-0 my-4">
                  <div className="flex-1 h-px bg-primary/10" />
                  <span className="text-[10px] text-primary font-medium bg-background border border-primary/10 py-2 px-3 rounded-full">
                    {new Date(message.createdAt).toLocaleDateString(undefined, {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                  <div className="flex-1 h-px bg-primary/10" />
                </div>
              )}
              {message.messageId === firstUnreadMessageId && (
                <div className="my-3 flex items-center gap-2 px-3" aria-label="Unread messages">
                  <div className="h-px flex-1 bg-primary/40" />
                  <span className="rounded-full bg-primary/10 px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    Unread messages
                  </span>
                  <div className="h-px flex-1 bg-primary/40" />
                </div>
              )}
              <MessageItem
                message={message}
                isOwnMessage={message.senderId === currentUserId}
                isAdmin={isAdmin}
                isConsecutive={isConsecutive}
                isLastInGroup={isLastInGroup}
                onStartEdit={onStartEditMessage}
                onDelete={onDeleteMessage}
                onReply={onReplyMessage}
                onReaction={onReactionMessage}
                onOpenDm={onOpenDm}
                onJumpToMessage={jumpToMessage}
              />
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Floating "down" button. Doubles as (1) the Telegram-style return through
          reply jumps when there's a return stack, and (2) a plain scroll-to-latest
          when the user has simply scrolled up away from the newest message. */}
      {(canGoBack || showJumpToBottom) && (
        <button
          type="button"
          onClick={canGoBack ? goBack : scrollToBottom}
          title={canGoBack ? 'Back to where you were' : 'Scroll to latest'}
          aria-label={canGoBack ? 'Back to where you were' : 'Scroll to latest'}
          className="absolute bottom-4 right-4 z-20 h-10 w-10 rounded-full bg-card border border-border shadow-lg flex items-center justify-center text-foreground hover:bg-muted transition-colors"
        >
          <ArrowDown className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
